import { useMutation } from '@tanstack/react-query';
import { includes } from 'lodash';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { prepareAssessmentResponse } from './assessment-response.prepare';
import { prepareTestInstanceResponse } from './test-instance-response.prepare';
import type {
  BaseTestInstanceResponse,
  PostAssessmentResponse,
  PostAssessmentResponseData,
  PostTestInstanceResponse
} from './types';
import { ResponseStatus } from './types';

const order = {};
const groups = [
  'testinstanceresponse-stats',
  'testinstanceresponse-responsecategories',
  'testinstanceresponsescategories-category',
  'testinstanceresponse-responseitems'
];
const include = 'token,responseCategories,responseItems';

/**
 * Fetch test-instance-responses
 * @param params can be used to customise/limit the test-instance-response retrieved
 * @returns the requested test-instance-response
 */
export const getTestInstanceResponses: GetMany<BaseTestInstanceResponse> = async (params) =>
  (
    await ASSIST_API.get('test-instance-responses', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch test-instance-response (singular)
 * @param params can be used to customise/limit the test-instance retrieved
 * @returns a single test-instance
 */
export const getTestInstanceResponse: GetOne<BaseTestInstanceResponse> = async (id, params) =>
  (await ASSIST_API.get(`test-instance-responses/${id}`, { params })).data;

/**
 * Hook implementation to fetch test-instance-responses
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-instance-responses matching request
 */
export const useTestInstanceResponses: AssistQuery.UseMany<BaseTestInstanceResponse> = (
  params,
  options
) =>
  useQueryAssist(
    ['test-instance-responses', params],
    () => getTestInstanceResponses(params),
    options
  );

/**
 * Given an testInstanceResponseId fetch and return the test instance response
 *  - only runs once testInstanceResponseId is provided
 *  - fetches the one testInstanceResponse
 *  - caches data
 * @param id testInstanceResponseId
 * @param params optional parameters to change requested data
 * @returns the matching testInstanceResponse
 */
export const useTestInstanceResponse: AssistQuery.UseOne<BaseTestInstanceResponse> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['test-instance-responses', { id, ...params }],
    () => getTestInstanceResponse(id, params),
    {
      ...options,
      enabled: !!id
    }
  );

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteTestInstanceResponse: AssistQuery.UseMutation<
  BaseTestInstanceResponse,
  number
> = (options) =>
  useMutation((id) => ASSIST_API.delete(`test-instance-responses/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['test-instance-responses']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });

/**
 * Create new assessment response (also saving response items)
 * - takes a test-instance-response and creates a POST request
 * @param options
 */
export const usePostAssessmentResponse: AssistQuery.UseMutation<
  PostAssessmentResponseData,
  PostAssessmentResponse
> = (options) =>
  useMutation(
    (data: PostAssessmentResponse) =>
      ASSIST_API.post('test-instance-responses', prepareAssessmentResponse(data), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-responses']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Create new test-instance-response
 * - takes a test-instance-response and creates a POST request
 * @param options
 */
export const usePostTestInstanceResponse: AssistQuery.UseMutation<
  BaseTestInstanceResponse,
  PostTestInstanceResponse
> = (options) =>
  useMutation(
    (data: PostTestInstanceResponse) =>
      ASSIST_API.post('test-instance-responses', prepareTestInstanceResponse(data), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-responses']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PatchTestInstanceResponseVariables = { id: number; data: Partial<PostTestInstanceResponse> };

/**
 * Updates a test-instance-response
 * - takes a test-instance-response and creates a PATCH request
 * @param options
 */
export const usePatchTestInstanceResponse: AssistQuery.UseMutation<
  BaseTestInstanceResponse,
  PatchTestInstanceResponseVariables
> = (options) =>
  useMutation(
    ({ id, data }: PatchTestInstanceResponseVariables) =>
      ASSIST_API.patch(`test-instance-responses/${id}`, prepareTestInstanceResponse(data), {
        params: requestParams({ order, groups, include })
      }),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-responses']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export const isFinished = (status?: ResponseStatus): boolean =>
  includes([ResponseStatus.Finished, ResponseStatus.Closed], status);

export const isStarted = (status?: ResponseStatus): boolean =>
  includes([ResponseStatus.Started], status);

export const transformStatus = (status?: string): ResponseStatus => {
  if (!includes(Object.values(ResponseStatus), status)) {
    return ResponseStatus.Registered;
  }
  return status as ResponseStatus;
};
